import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  fetchNotificationHistory,
  logoutDevice,
  registerDevice,
  markNotificationRead,
  markAllNotificationsRead,
  fetchUnreadNotificationCount,
} from '../../services/notificationService';
import { useAuthStore } from '../../stores/useAuthStore';
import { useNotificationStore } from '../../stores/useNotificationStore';
import { parseNotificationParams } from '../notificationActionResolver';

/** Stable query key for notification history cache. */
export const NOTIFICATION_QUERY_KEY = ['notificationHistory'];

/**
 * Normalise a single notification from the backend response into the
 * flat shape consumed by the UI:
 *   { id, title, message, isRead, createdAt, data }
 */
const normaliseNotification = (raw) => {
  // New API shape: { notification: { title, body }, data: { params, action_type, id }, isRead }
  if (raw.notification) {
    const { title, body } = raw.notification;
    const data = raw.data || {};
    return {
      id: data.id ? Number(data.id) : Date.now(),
      title: title || 'Notification',
      message: body || '',
      isRead: raw.isRead ?? false,
      canRedirect: raw.canRedirect || false,
      createdAt: raw.createdAt || data.createdAt || new Date().toISOString(),
      data: {
        action_type: data.action_type || null,
        params: parseNotificationParams(data.params),
        id: data.id || null,
      },
    };
  }

  // Legacy / already-normalised shape — passthrough
  return raw;
};

/**
 * Fetches notification history from the backend and syncs it to Zustand.
 * Only runs when the user is authenticated.
 * React Query deduplicates this — safe to call from multiple components.
 */
export const useNotificationHistory = (page = 0, size = 100, filter = 'ALL') => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setNotifications = useNotificationStore(
    (state) => state.setNotifications
  );

  const query = useQuery({
    queryKey: [...NOTIFICATION_QUERY_KEY, page, size, filter],
    queryFn: async () => {
      const res = await fetchNotificationHistory(page, size, filter);
      const data = res.data;
      let rawList = [];
      let totalPages = 1;
      let totalElements = 0;

      if (data) {
        if (Array.isArray(data)) {
          rawList = data;
          totalPages = 1;
          totalElements = data.length;
        } else if (data.content && Array.isArray(data.content)) {
          rawList = data.content;
          totalPages = data.totalPages ?? 1;
          totalElements = data.totalElements ?? 0;
        } else if (Array.isArray(data.notifications)) {
          rawList = data.notifications;
          totalPages = data.totalPages ?? 1;
          totalElements = data.totalElements ?? 0;
        }
      }

      const normalizedList = rawList.map(normaliseNotification);
      return {
        notifications: normalizedList,
        totalPages,
        totalElements,
      };
    },
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Sync backend data into Zustand whenever the query data changes.
  useEffect(() => {
    if (query.data) {
      const list = Array.isArray(query.data) ? query.data : (query.data.notifications || []);
      setNotifications(list);
    }
  }, [query.data, setNotifications]);

  return query;
};

/**
 * Mutation to register this device's FCM token with the backend.
 */
export const useRegisterDevice = () =>
  useMutation({ mutationFn: registerDevice });

/**
 * Mutation to unregister the FCM token on logout.
 * Clears the local token and removes the cached notification history.
 */
export const useLogoutDevice = () => {
  const queryClient = useQueryClient();
  const clearFcmToken = useNotificationStore((state) => state.clearFcmToken);

  return useMutation({
    mutationFn: logoutDevice,
    onSuccess: () => {
      clearFcmToken();
      queryClient.removeQueries({ queryKey: NOTIFICATION_QUERY_KEY });
    },
    onError: () => {
      // Even on API error, clear local token to prevent stale registration
      clearFcmToken();
    },
  });
};

/**
 * Mutation to mark a single notification as read.
 */
export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();
  const markAsReadLocal = useNotificationStore((state) => state.markAsRead);

  return useMutation({
    mutationFn: markNotificationRead,
    onMutate: async (id) => {
      // Optimistic update locally
      markAsReadLocal(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['unreadNotificationCount'] });
    },
  });
};

/**
 * Mutation to mark all notifications as read.
 */
export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();
  const markAllAsReadLocal = useNotificationStore((state) => state.markAllAsRead);

  return useMutation({
    mutationFn: markAllNotificationsRead,
    onMutate: async () => {
      // Optimistic update locally
      markAllAsReadLocal();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['unreadNotificationCount'] });
    },
  });
};

/**
 * Fetch the total unread notification count.
 */
export const useUnreadNotificationCount = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery({
    queryKey: ['unreadNotificationCount'],
    queryFn: async () => {
      const res = await fetchUnreadNotificationCount();
      return res.data?.unreadCount ?? 0;
    },
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};
